import { useState, useCallback, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import { getCurrentClinicCustomerId, getCurrentClinicId } from '@/lib/utils/clinic-context';
import { generateUUID } from '@/lib/utils/uuid';
import type { Database } from '@/integrations/supabase/types';
import { invalidateReportsCache } from '@/lib/react-query/invalidateReportsCache';
import { invalidateAppointmentUiCache } from '@/lib/react-query/invalidateAppointmentUiCache';
import { reconcileAppointmentFinancial } from '@/lib/services/appointment-financial-reconciliation';
import type { SuggestedPackageCharge } from '@/lib/utils/suggested-package-charge';
import { useTranslation } from 'react-i18next';
import { createAdHocSaleItemSchema, type AdHocSaleItemInput } from '@/lib/sales/adHocSaleItem';
import { itemCanEarnCommission } from '@/lib/commissions/groups';
import { canMergeIntoLine, clearCartItemPets, normalizeItemPetId, resolveItemPetId } from '@/lib/sales/cart-pets';

/**
 * The sale belongs to the clinic the user is currently working in (profiles.current_clinic_id),
 * not to any clinic of the customer: commission accrual, reports and RLS depend on sales.clinic_id.
 */

/** Dinheiro sempre em centavos: o total que a tela mostra é o que se grava e o que se cobra. */
const toCents = (value: number) => Math.round((value + Number.EPSILON) * 100) / 100;
export async function resolveSaleClinicId(): Promise<string | null> {
  try {
    return await getCurrentClinicId();
  } catch {
    return null;
  }
}

export interface CartItem {
  id: string;
  /** `null` nas linhas avulsas e automáticas de estadia, que não vêm do catálogo. */
  itemId: string | null;
  name: string;
  itemType?: "PRODUTO" | "SERVICO" | "PACOTE" | null;
  quantity: number;
  unitPrice: number;
  discount: number;
  total: number;
  barcode?: string;
  /** True when the item belongs to a commission group (the commission itself is computed by the database). */
  canEarnCommission?: boolean;
  professionalId?: string | null;
  professionalName?: string | null;
  /** Pet desta linha quando difere do pet da venda; `null`/ausente = segue o pet escolhido na busca. */
  petId?: string | null;
}

export type PaymentMethod = 'DINHEIRO' | 'CARTAO_DEBITO' | 'CARTAO_CREDITO' | 'PIX' | 'A_RECEBER';

export interface Payment {
  id: string;
  method: PaymentMethod;
  amount: number;
}

export interface SuggestedPackageChargeRow extends SuggestedPackageCharge {
  id: string;
  included: boolean;
  amount: number;
}

type PaidPackageCommitPayload = {
  appointment_patient_package_id: string;
  charged_amount: number;
};

type PatientPackageChargePayload = {
  patient_package_id: string;
  charged_amount: number;
};

export type DiscountType = 'percent' | 'fixed';

export interface SaleContext {
  petId: string | null;
  ownerId: string | null;
  buyerCpf: string | null;
  appointmentId: string | null;
}

export type SaleWriteMode = { mode: "insert" } | { mode: "update"; saleId: string };

/**
 * A comanda de estadia já é uma venda ABERTA no banco. Retomá-la significa
 * fechar aquela linha, não criar outra — senão a receita aparece duas vezes.
 */
export function resolveSaleWriteMode(resumeSaleId: string | null): SaleWriteMode {
  return resumeSaleId ? { mode: "update", saleId: resumeSaleId } : { mode: "insert" };
}

/** Linha de sale_items com a coluna nova (migration 20261013120000); o types.ts gerado ainda não a tem. */
export type SaleItemRow = Database["public"]["Tables"]["sale_items"]["Insert"] & { pet_id: string | null };

export function buildSaleItemRows(
  saleId: string,
  cart: CartItem[],
  packageCharges: SuggestedPackageChargeRow[],
  salePetId: string | null,
): SaleItemRow[] {
  const saleItems = cart.map((item) => ({
    sale_id: saleId,
    item_id: item.itemId,
    item_name: item.name,
    quantity: item.quantity,
    unit_price: item.unitPrice,
    discount_amount: (item.unitPrice * item.quantity * item.discount) / 100,
    line_total: item.total,
    professional_id: item.professionalId ?? null,
    professional_name_snapshot: item.professionalName ?? null,
    pet_id: resolveItemPetId(item, salePetId),
  }));
  const packageSaleItems = packageCharges.map((row) => ({
    sale_id: saleId,
    item_id: row.itemId ?? null,
    item_name: row.title,
    quantity: 1,
    unit_price: row.included ? row.amount : 0,
    discount_amount: 0,
    line_total: row.included ? row.amount : 0,
    // venda sem paciente (ex.: paciente limpo antes de finalizar) não grava pet nem na cobrança de pacote
    pet_id: salePetId ? (row.petId ?? salePetId) : null,
  }));
  return [...saleItems, ...packageSaleItems];
}

export interface ComandaCartLine {
  itemId: string | null;
  name: string;
  quantity: number;
  unitPrice: number;
}

export function buildComandaCartItems(lines: ComandaCartLine[]): CartItem[] {
  return lines.map((line) => ({
    id: generateUUID(),
    itemId: line.itemId,
    name: line.name,
    quantity: line.quantity,
    unitPrice: line.unitPrice,
    discount: 0,
    total: line.quantity * line.unitPrice,
  }));
}

export const useSales = () => {
  const { toast } = useToast();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const { t } = useTranslation("erp");
  const [cart, setCart] = useState<CartItem[]>([]);
  const [payments, setPayments] = useState<Payment[]>([]);
  const [suggestedPackageCharges, setSuggestedPackageChargesState] = useState<SuggestedPackageChargeRow[]>([]);
  const [discountType, setDiscountType] = useState<DiscountType>('fixed');
  const [discountValue, setDiscountValue] = useState(0);
  const [resumeSaleId, setResumeSaleId] = useState<string | null>(null);
  const [saleContext, setSaleContext] = useState<SaleContext>({
    petId: null,
    ownerId: null,
    buyerCpf: null,
    appointmentId: null,
  });

  const setPetId = useCallback((petId: string | null) => {
    setSaleContext((prev) => ({ ...prev, petId }));
    // escolher (ou limpar) o paciente na busca devolve todos os itens a ele
    setCart((prev) => clearCartItemPets(prev));
  }, []);
  const setOwnerId = useCallback((ownerId: string | null) => {
    setSaleContext((prev) => ({ ...prev, ownerId }));
  }, []);
  const setBuyerCpf = useCallback((buyerCpf: string | null) => {
    setSaleContext((prev) => ({ ...prev, buyerCpf }));
  }, []);
  const setAppointmentId = useCallback((appointmentId: string | null) => {
    setSaleContext((prev) => ({ ...prev, appointmentId }));
  }, []);
  const setSaleContextFull = useCallback((ctx: Partial<SaleContext>) => {
    setSaleContext((prev) => ({ ...prev, ...ctx }));
  }, []);

  const setResumeSale = useCallback((saleId: string | null) => {
    setResumeSaleId(saleId);
  }, []);

  const setCartFromComandaLines = useCallback((lines: ComandaCartLine[]) => {
    setCart(buildComandaCartItems(lines));
  }, []);

  const addItemToCart = useCallback((item: { id: string; name: string; sale_price: number; barcode?: string; type?: "PRODUTO" | "SERVICO" | "PACOTE" | null; product_subtype_id?: string | null; service_group_id?: string | null; package_subtype_id?: string | null; defaultProfessionalId?: string | null; defaultProfessionalName?: string | null }, quantity = 1) => {
    setCart((prev) => {
      const existingItem = prev.find((i) => canMergeIntoLine(i, item.id));

      if (existingItem) {
        return prev.map((i) =>
          i.id === existingItem.id
            ? { ...i, quantity: i.quantity + quantity, total: (i.quantity + quantity) * i.unitPrice * (1 - i.discount / 100) }
            : i
        );
      }

      const newItem: CartItem = {
        id: generateUUID(),
        itemId: item.id,
        name: item.name,
        itemType: item.type ?? null,
        quantity,
        unitPrice: item.sale_price,
        discount: 0,
        total: quantity * item.sale_price,
        barcode: item.barcode,
        canEarnCommission: itemCanEarnCommission(item),
        professionalId: item.defaultProfessionalId ?? null,
        professionalName: item.defaultProfessionalName ?? null,
      };

      return [...prev, newItem];
    });
  }, []);

  const addAdHocItemToCart = useCallback((input: AdHocSaleItemInput) => {
    const { name, quantity, unitPrice } = createAdHocSaleItemSchema(t).parse(input);
    const [line] = buildComandaCartItems([{ name, quantity, unitPrice, itemId: null }]);
    setCart((prev) => [...prev, line]);
  }, [t]);

  const updateCartItemProfessional = useCallback((cartItemId: string, professionalId: string | null, professionalName: string | null) => {
    setCart((prev) =>
      prev.map((item) =>
        item.id === cartItemId
          ? { ...item, professionalId, professionalName }
          : item
      )
    );
  }, []);

  const updateCartItemPet = useCallback((cartItemId: string, petId: string | null) => {
    setCart((prev) =>
      prev.map((item) =>
        item.id === cartItemId ? { ...item, petId: normalizeItemPetId(petId, saleContext.petId) } : item,
      ),
    );
  }, [saleContext.petId]);

  const removeItemFromCart = useCallback((id: string) => {
    setCart((prev) => prev.filter((item) => item.id !== id));
  }, []);

  const updateItemQuantity = useCallback((id: string, quantity: number) => {
    if (quantity <= 0) {
      removeItemFromCart(id);
      return;
    }

    setCart((prev) =>
      prev.map((item) =>
        item.id === id
          ? { ...item, quantity, total: quantity * item.unitPrice * (1 - item.discount / 100) }
          : item
      )
    );
  }, [removeItemFromCart]);

  const updateItemDiscount = useCallback((id: string, discount: number) => {
    setCart((prev) =>
      prev.map((item) =>
        item.id === id
          ? { ...item, discount, total: item.quantity * item.unitPrice * (1 - discount / 100) }
          : item
      )
    );
  }, []);

  const updateItemUnitPrice = useCallback((id: string, unitPrice: number) => {
    const normalizedUnitPrice = Math.max(0, unitPrice);
    setCart((prev) =>
      prev.map((item) =>
        item.id === id
          ? {
              ...item,
              unitPrice: normalizedUnitPrice,
              total: item.quantity * normalizedUnitPrice * (1 - item.discount / 100),
            }
          : item,
      ),
    );
  }, []);

  const addPayment = useCallback((payment: Omit<Payment, 'id'>) => {
    const newPayment: Payment = {
      id: generateUUID(),
      ...payment,
    };
    setPayments((prev) => [...prev, newPayment]);
  }, []);

  const removePayment = useCallback((id: string) => {
    setPayments((prev) => prev.filter((p) => p.id !== id));
  }, []);

  const getSubtotal = useCallback(() => {
    const cartTotal = cart.reduce((sum, item) => sum + item.total, 0);
    const packageTotal = suggestedPackageCharges.reduce(
      (sum, row) => sum + (row.included ? row.amount : 0),
      0,
    );
    return toCents(cartTotal + packageTotal);
  }, [cart, suggestedPackageCharges]);

  const getDiscountAmount = useCallback(() => {
    const subtotal = getSubtotal();
    if (discountType === 'percent') {
      return toCents(subtotal * (Math.min(100, Math.max(0, discountValue)) / 100));
    }
    return toCents(Math.min(discountValue, subtotal));
  }, [discountType, discountValue, getSubtotal]);

  const getTotal = useCallback(() => {
    return toCents(Math.max(0, getSubtotal() - getDiscountAmount()));
  }, [getSubtotal, getDiscountAmount]);

  const getTotalPaid = useCallback(() => {
    return toCents(payments.reduce((sum, p) => sum + p.amount, 0));
  }, [payments]);

  const getChange = useCallback(() => {
    return toCents(Math.max(0, getTotalPaid() - getTotal()));
  }, [getTotalPaid, getTotal]);

  const completingRef = useRef(false);
  const [isCompleting, setIsCompleting] = useState(false);

  const clearCart = useCallback(() => {
    completingRef.current = false;
    setIsCompleting(false);
    setResumeSaleId(null);
    setCart([]);
    setPayments([]);
    setSuggestedPackageChargesState([]);
    setDiscountType('fixed');
    setDiscountValue(0);
    setSaleContext({ petId: null, ownerId: null, buyerCpf: null, appointmentId: null });
  }, []);

  const setSuggestedPackageCharges = useCallback((rows: SuggestedPackageCharge[] = []) => {
    setSuggestedPackageChargesState(
      rows.map((row) => ({
        ...row,
        id: generateUUID(),
        included: row.billingIntent === "integral_now" || row.billingIntent === "partial_now",
        amount: Math.max(0, row.suggestedChargeAmount ?? 0),
      })),
    );
  }, []);

  const updateSuggestedPackageCharge = useCallback((id: string, updates: Partial<Pick<SuggestedPackageChargeRow, "included" | "amount">>) => {
    setSuggestedPackageChargesState((prev) =>
      prev.map((row) =>
        row.id === id
          ? {
              ...row,
              included: updates.included ?? row.included,
              amount: updates.amount == null ? row.amount : Math.max(0, updates.amount),
            }
          : row,
      ),
    );
  }, []);

  const removeSuggestedPackageCharge = useCallback((id: string) => {
    setSuggestedPackageChargesState((prev) => prev.filter((row) => row.id !== id));
  }, []);

  const addSuggestedPackageCharge = useCallback((row: SuggestedPackageCharge) => {
    setSuggestedPackageChargesState((prev) => {
      const duplicateIndex = prev.findIndex((current) => {
        if (row.patientPackageId && current.patientPackageId === row.patientPackageId) return true;
        if (row.appointmentPatientPackageId && current.appointmentPatientPackageId === row.appointmentPatientPackageId) return true;
        if (!row.patientPackageId && !row.appointmentPatientPackageId) {
          if (row.catalogPackageId && current.catalogPackageId === row.catalogPackageId) return true;
          if (!row.catalogPackageId && row.itemId && current.itemId === row.itemId && current.packageMode === row.packageMode) return true;
        }
        return false;
      });

      const nextRow: SuggestedPackageChargeRow = {
        ...row,
        id: duplicateIndex >= 0 ? prev[duplicateIndex].id : generateUUID(),
        included: row.billingIntent === "integral_now" || row.billingIntent === "partial_now",
        amount: Math.max(0, row.suggestedChargeAmount ?? 0),
      };

      if (duplicateIndex < 0) return [...prev, nextRow];

      return prev.map((current, index) => (index === duplicateIndex ? nextRow : current));
    });
  }, []);

  const retainSuggestedPackageCharges = useCallback((predicate: (row: SuggestedPackageChargeRow) => boolean) => {
    setSuggestedPackageChargesState((prev) => prev.filter(predicate));
  }, []);

  /** Build cart from appointment_items + current catalog prices (for finalized appointments). */
  const setCartFromAppointmentItems = useCallback(
    (
      appointmentItems: Array<{ item_id: string; qty: number; title_snapshot?: string | null; item?: { name?: string; sale_price?: number | null; type?: "PRODUTO" | "SERVICO" | "PACOTE" | null } | null }>,
      catalogItems: Array<{ id: string; name: string; sale_price?: number | null; barcode?: string | null; type?: "PRODUTO" | "SERVICO" | "PACOTE" | null; product_subtype_id?: string | null; service_group_id?: string | null; package_subtype_id?: string | null }>,
      appointment?: { vet_user_id?: string | null; vet?: { full_name?: string | null } | null } | null,
    ) => {
      const normalized = (appointmentItems ?? [])
        .map((ai) => {
          const itemId = ai.item_id;
          const catalogItem = catalogItems.find((c) => c.id === itemId);
          const unitPrice = catalogItem?.sale_price ?? ai.item?.sale_price ?? 0;
          const quantity = ai.qty ?? 1;
          const name = ai.title_snapshot?.trim() || catalogItem?.name || ai.item?.name || 'Item';
          return {
            id: generateUUID(),
            itemId,
            name,
            itemType: catalogItem?.type ?? ai.item?.type ?? null,
            quantity,
            unitPrice,
            discount: 0,
            total: quantity * unitPrice,
            barcode: catalogItem?.barcode ?? undefined,
            canEarnCommission: catalogItem ? itemCanEarnCommission(catalogItem) : false,
            professionalId: appointment?.vet_user_id ?? null,
            professionalName: appointment?.vet?.full_name?.trim() || null,
          } as CartItem;
        })
        .filter((item): item is CartItem => item !== null);
      setCart(normalized);
    },
    [],
  );

  const completeSale = useCallback(async (options?: { allowReceivable?: boolean }) => {
    // um clique a mais (ou o "Salvar venda" logo depois do "Finalizar") não grava a venda de novo
    if (completingRef.current) return null;
    completingRef.current = true;
    setIsCompleting(true);
    let saved = false;
    try {
      const total = getTotal();
      const currentTotalPaid = getTotalPaid();
      const remainingToReceivable = Math.max(0, total - currentTotalPaid);
      const paymentsToPersist =
        options?.allowReceivable && remainingToReceivable > 0
          ? [
              ...payments,
              {
                id: generateUUID(),
                method: 'A_RECEBER' as const,
                amount: remainingToReceivable,
              },
            ]
          : payments;
      const totalPaid = toCents(paymentsToPersist.reduce((sum, payment) => sum + payment.amount, 0));
      const overpayment = toCents(Math.max(0, totalPaid - total));
      const cashPaid = paymentsToPersist
        .filter((payment) => payment.method === 'DINHEIRO')
        .reduce((sum, payment) => sum + payment.amount, 0);

      if (overpayment > 0 && cashPaid < overpayment) {
        toast({
          title: t("phase8.sales.overpaymentTitle"),
          description: t("phase8.sales.overpaymentDescription", { amount: overpayment.toFixed(2) }),
          variant: "destructive",
        });
        return null;
      }

      if (totalPaid < total) {
        toast({
          title: t("phase8.sales.insufficientPaymentTitle"),
          description: t("phase8.sales.insufficientPaymentDescription", { amount: (total - totalPaid).toFixed(2) }),
          variant: "destructive",
        });
        return null;
      }

      const customerId = await getCurrentClinicCustomerId();

      const saleClinicId = await resolveSaleClinicId();

      if (!saleClinicId) {
        toast({
          title: t("phase8.sales.errorTitle"),
          description: t("phase8.sales.noActiveClinicDescription"),
          variant: "destructive",
        });
        return null;
      }

      const saleNumber = `V${Date.now()}`;
      const subtotal = getSubtotal();
      const discountAmount = getDiscountAmount();
      const discountPercentage = discountType === 'percent' ? discountValue : null;
      const currentUserId = user?.id ?? null;
      const saleInsert: Database['public']['Tables']['sales']['Insert'] = {
        sale_number: saleNumber,
        clinic_id: saleClinicId,
        customer_id: customerId,
        appointment_id: saleContext.appointmentId || null,
        pet_id: saleContext.petId || null,
        owner_id: saleContext.ownerId || null,
        buyer_cpf: saleContext.buyerCpf?.replace(/\D/g, '') || null,
        subtotal,
        discount_amount: discountAmount,
        discount_percentage: discountPercentage,
        total,
        status: 'PAGA',
        created_by: currentUserId,
      };

      const writeMode = resolveSaleWriteMode(resumeSaleId);
      let sale: { id: string };

      if (writeMode.mode === "update") {
        // A comanda ABERTA da estadia vira PAGA; `stay_reservation_id` fica intacto.
        const { data: updated, error: updateError } = await supabase
          .from('sales')
          .update({
            customer_id: customerId,
            appointment_id: saleContext.appointmentId || null,
            pet_id: saleContext.petId || null,
            owner_id: saleContext.ownerId || null,
            buyer_cpf: saleContext.buyerCpf?.replace(/\D/g, '') || null,
            subtotal,
            discount_amount: discountAmount,
            discount_percentage: discountPercentage,
            total,
            status: 'PAGA',
          })
          .eq('id', writeMode.saleId)
          .select()
          .single();
        if (updateError) throw updateError;

        // O carrinho pode ter sido editado no PDV: as linhas antigas saem inteiras.
        const { error: clearItemsError } = await supabase
          .from('sale_items')
          .delete()
          .eq('sale_id', writeMode.saleId);
        if (clearItemsError) throw clearItemsError;

        sale = updated;
      } else {
        // Após o pagamento a venda fica com status PAGA
        const { data: inserted, error: saleError } = await supabase
          .from('sales')
          .insert(saleInsert)
          .select()
          .single();
        if (saleError) throw saleError;
        sale = inserted;
      }

      const saleItemRows = buildSaleItemRows(sale.id, cart, suggestedPackageCharges, saleContext.petId ?? null);

      // sale_items.pet_id ainda não está no types.ts gerado (que não se edita): insert sem tipo, como no resto do código.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { error: itemsError } = await (supabase as any)
        .from('sale_items')
        .insert(saleItemRows);

      if (itemsError) throw itemsError;

      // Decrement product_stock for stock-controlled catalog items.
      // For PACOTE, decrement stock of internal PRODUTO components when template exists.
      const stockCandidates: Array<{ itemId: string; quantity: number }> = [
        ...cart
          .filter((item): item is CartItem & { itemId: string } => Boolean(item.itemId))
          .map((item) => ({ itemId: item.itemId, quantity: item.quantity })),
        ...suggestedPackageCharges
          .filter((row) => row.included && Boolean(row.itemId))
          .map((row) => ({ itemId: row.itemId as string, quantity: 1 })),
      ];

      const itemIds = [...new Set(stockCandidates.map((entry) => entry.itemId))];
      if (itemIds.length > 0) {
        const { data: items } = await supabase
          .from('items')
          .select('id, type')
          .in('id', itemIds);

        const itemTypeById = new Map((items ?? []).map((item) => [item.id, item.type]));
        const quantityToDecrementByItemId = new Map<string, number>();
        const packageQuantityByItemId = new Map<string, number>();

        for (const candidate of stockCandidates) {
          const itemType = itemTypeById.get(candidate.itemId);
          if (itemType === 'PRODUTO') {
            const current = quantityToDecrementByItemId.get(candidate.itemId) ?? 0;
            quantityToDecrementByItemId.set(candidate.itemId, current + candidate.quantity);
            continue;
          }

          if (itemType === 'PACOTE') {
            const current = packageQuantityByItemId.get(candidate.itemId) ?? 0;
            packageQuantityByItemId.set(candidate.itemId, current + candidate.quantity);
          }
        }

        if (packageQuantityByItemId.size > 0) {
          const packageItemIds = [...packageQuantityByItemId.keys()];

          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const { data: packageTemplates, error: packageTemplatesError } = await (supabase as any)
            .from("catalog_packages")
            .select("id, item_id, items:catalog_package_items(item_id, sessions_total)")
            .eq("customer_id", customerId)
            .is("deleted_at", null)
            .in("item_id", packageItemIds);

          if (!packageTemplatesError) {
            const templateByPackageItemId = new Map<string, {
              id: string;
              item_id: string;
              items?: Array<{ item_id?: string | null; sessions_total?: number | null }>;
            }>(
              ((packageTemplates ?? []) as Array<{
                id: string;
                item_id: string;
                items?: Array<{ item_id?: string | null; sessions_total?: number | null }>;
              }>).map((template) => [template.item_id, template]),
            );

            const componentItemIds = [
              ...new Set<string>(
                (packageTemplates ?? [])
                  .flatMap((template: { items?: Array<{ item_id?: string | null }> }) => template.items ?? [])
                  .map((component) => component.item_id)
                  .filter((componentItemId): componentItemId is string => Boolean(componentItemId)),
              ),
            ];

            let componentTypeById = new Map<string, string | null>();
            if (componentItemIds.length > 0) {
              const { data: componentItems } = await supabase
                .from("items")
                .select("id, type")
                .in("id", componentItemIds);
              componentTypeById = new Map((componentItems ?? []).map((component) => [component.id, component.type]));
            }

            for (const [packageItemId, packageQuantity] of packageQuantityByItemId.entries()) {
              const template = templateByPackageItemId.get(packageItemId);
              if (!template?.items?.length) {
                // Legacy fallback: if package has no template, decrement package stock itself.
                const current = quantityToDecrementByItemId.get(packageItemId) ?? 0;
                quantityToDecrementByItemId.set(packageItemId, current + packageQuantity);
                continue;
              }

              for (const component of template.items) {
                if (!component.item_id) continue;
                if (componentTypeById.get(component.item_id) !== 'PRODUTO') continue;

                const perPackageQuantity = Math.max(0, Number(component.sessions_total ?? 1));
                if (perPackageQuantity <= 0) continue;

                const totalComponentQuantity = perPackageQuantity * packageQuantity;
                const current = quantityToDecrementByItemId.get(component.item_id) ?? 0;
                quantityToDecrementByItemId.set(component.item_id, current + totalComponentQuantity);
              }
            }
          } else {
            // Fallback to previous behavior if template query fails (permissions/temporary issues).
            for (const [packageItemId, packageQuantity] of packageQuantityByItemId.entries()) {
              const current = quantityToDecrementByItemId.get(packageItemId) ?? 0;
              quantityToDecrementByItemId.set(packageItemId, current + packageQuantity);
            }
          }
        }

        for (const [itemId, quantityToDecrement] of quantityToDecrementByItemId.entries()) {
          if (quantityToDecrement <= 0) continue;
          const { data: stock } = await supabase
            .from('product_stock')
            .select('id, quantity')
            .eq('clinic_id', saleClinicId)
            .eq('item_id', itemId)
            .maybeSingle();
          if (stock) {
            const newQty = Math.max(0, (stock.quantity ?? 0) - quantityToDecrement);
            await supabase
              .from('product_stock')
              .update({ quantity: newQty })
              .eq('id', stock.id);
          }
        }
      }

      const nowIso = new Date().toISOString();
      const paymentRecords: Array<Database["public"]["Tables"]["sale_payments"]["Insert"]> = paymentsToPersist.map((p) => ({
        sale_id: sale.id,
        method: p.method,
        amount: p.amount,
        created_by: currentUserId,
        received_at: p.method === "A_RECEBER" ? null : nowIso,
      }));

      // cortesia (100% de desconto) não tem pagamento: a venda fica registrada com total zero
      if (paymentRecords.length > 0) {
        const { error: paymentsError } = await supabase
          .from('sale_payments')
          .insert(paymentRecords);

        if (paymentsError) throw paymentsError;
      }

      const paidPatientPackageCharges: PatientPackageChargePayload[] = Object.values(
        suggestedPackageCharges
          .filter((row) => row.included && row.amount > 0 && row.patientPackageId)
          .reduce<Record<string, PatientPackageChargePayload>>((acc, row) => {
            const packageId = row.patientPackageId as string;
            const current = acc[packageId];
            acc[packageId] = {
              patient_package_id: packageId,
              charged_amount: (current?.charged_amount ?? 0) + row.amount,
            };
            return acc;
          }, {}),
      );

      if (paidPatientPackageCharges.length > 0) {
        for (const charge of paidPatientPackageCharges) {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const { data: patientPackage, error: patientPackageError } = await (supabase as any)
            .from("patient_packages")
            .select("id, full_price, amount_paid, deleted_at")
            .eq("id", charge.patient_package_id)
            .maybeSingle();

          if (patientPackageError) throw patientPackageError;
          if (!patientPackage || patientPackage.deleted_at) continue;

          const fullPrice = Math.max(0, Number(patientPackage.full_price ?? 0));
          const currentAmountPaid = Math.max(0, Number(patientPackage.amount_paid ?? 0));
          const nextAmountPaid = Math.min(fullPrice, currentAmountPaid + charge.charged_amount);
          const nextOutstandingAmount = Math.max(fullPrice - nextAmountPaid, 0);
          const nextStatus =
            nextOutstandingAmount <= 0
              ? "pago"
              : nextAmountPaid > 0
                ? "parcial"
                : "pendente";

          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const { error: updatePatientPackageError } = await (supabase as any)
            .from("patient_packages")
            .update({
              amount_paid: nextAmountPaid,
              outstanding_amount: nextOutstandingAmount,
              status_financeiro: nextStatus,
            })
            .eq("id", charge.patient_package_id);

          if (updatePatientPackageError) throw updatePatientPackageError;
        }
      }

      if (saleContext.appointmentId) {
        const appointmentId = saleContext.appointmentId;
        const paidPackageCharges: PaidPackageCommitPayload[] = suggestedPackageCharges
          .filter((row) => row.packageMode === "new" && row.included && row.amount > 0 && row.appointmentPatientPackageId)
          .map((row) => ({
            appointment_patient_package_id: row.appointmentPatientPackageId as string,
            charged_amount: row.amount,
          }));

        if (paidPackageCharges.length > 0) {
          const { error: commitPaidPackagesError } = await supabase.rpc(
            "commit_paid_appointment_patient_packages",
            {
              p_appointment_id: appointmentId,
              p_sale_id: sale.id,
              p_package_charges: paidPackageCharges,
            },
          );

          if (commitPaidPackagesError) {
            console.error('Error activating paid appointment packages:', commitPaidPackagesError);
            toast({
              title: t("phase8.sales.warningTitle"),
              description: t("phase8.sales.paidPackagesWarningDescription"),
            });
          } else {
            queryClient.invalidateQueries({ queryKey: ["appointment-patient-packages", appointmentId] });
            queryClient.invalidateQueries({ queryKey: ["patient-packages"] });
          }
        }

        try {
          await reconcileAppointmentFinancial(appointmentId);
          invalidateAppointmentUiCache(queryClient, { appointmentId, clearLists: true });
        } catch (appointmentFinancialError) {
          console.error('Error reconciling appointment financial status:', appointmentFinancialError);
          toast({
            title: t("phase8.sales.warningTitle"),
            description: t("phase8.sales.appointmentFinancialWarningDescription"),
          });
        }
      }

      toast(
        total === 0
          ? { title: t("phase8.sales.courtesyTitle"), description: t("phase8.sales.courtesyDescription", { saleNumber }) }
          : { title: t("phase8.sales.completedTitle"), description: t("phase8.sales.completedDescription", { saleNumber }) },
      );

      queryClient.invalidateQueries({ queryKey: ["finalized-appointments"] });
      queryClient.invalidateQueries({ queryKey: ["sales-list"] });
      queryClient.invalidateQueries({ queryKey: ["patient-packages"] });
      if (saleContext.appointmentId) {
        queryClient.invalidateQueries({ queryKey: ["sale-by-appointment", saleContext.appointmentId] });
      }
      invalidateReportsCache(queryClient);

      saved = true;
      return sale;
    } catch (error) {
      console.error('Error completing sale:', error);
      toast({
        title: t("phase8.sales.completeErrorTitle"),
        description: t("phase8.sales.completeErrorDescription"),
        variant: "destructive",
      });
      return null;
    } finally {
      // gravada, a trava só sai quando o carrinho for limpo (clearCart); senão, sai já
      if (!saved) {
        completingRef.current = false;
        setIsCompleting(false);
      }
    }
  }, [
    cart,
    payments,
    suggestedPackageCharges,
    discountType,
    discountValue,
    saleContext,
    resumeSaleId,
    user?.id,
    queryClient,
    getSubtotal,
    getDiscountAmount,
    getTotal,
    getTotalPaid,
    toast,
    t,
  ]);

  return {
    cart,
    payments,
    suggestedPackageCharges,
    discountType,
    discountValue,
    setDiscountType,
    setDiscountValue,
    saleContext,
    setPetId,
    setOwnerId,
    setBuyerCpf,
    setAppointmentId,
    setSaleContextFull,
    resumeSaleId,
    setResumeSale,
    setCartFromComandaLines,
    addItemToCart,
    addAdHocItemToCart,
    updateCartItemProfessional,
    updateCartItemPet,
    removeItemFromCart,
    updateItemQuantity,
    updateItemDiscount,
    updateItemUnitPrice,
    addPayment,
    removePayment,
    getSubtotal,
    getDiscountAmount,
    getTotal,
    getTotalPaid,
    getChange,
    clearCart,
    setSuggestedPackageCharges,
    addSuggestedPackageCharge,
    retainSuggestedPackageCharges,
    updateSuggestedPackageCharge,
    removeSuggestedPackageCharge,
    setCartFromAppointmentItems,
    completeSale,
    isCompleting,
  };
};
